import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import { getUsers, createUser, updateUser, deleteUser } from '../../api/admin/client';
import { useAuth } from '../../hooks/useAuth';

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'writer' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await getUsers();
      setUsers(data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (user = null) => {
    if (user) {
      setEditingId(user.id);
      setForm({
        name: user.name,
        email: user.email,
        password: '',
        role: user.role
      });
    } else {
      setEditingId(null);
      setForm({ name: '', email: '', password: '', role: 'writer' });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm({ name: '', email: '', password: '', role: 'writer' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    if (!editingId && !form.password) {
      alert('Password is required for new users');
      return;
    }

    try {
      setSaving(true);
      const data = { ...form };
      if (!data.password) delete data.password;

      if (editingId) {
        await updateUser(editingId, data);
      } else {
        await createUser(data);
      }
      closeModal();
      fetchUsers();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (id === currentUser.id) {
      alert('You cannot delete your own account');
      return;
    }
    if (!window.confirm(`Delete user "${name}"? This cannot be undone.`)) return;

    try {
      await deleteUser(id);
      fetchUsers();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleToggleActive = async (user) => {
    if (user.id === currentUser.id) {
      alert('You cannot deactivate your own account');
      return;
    }

    try {
      await updateUser(user.id, { isActive: !user.isActive });
      fetchUsers();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      admin: 'admin-badge-danger',
      editor: 'admin-badge-warning',
      writer: 'admin-badge-info'
    };
    return badges[role] || 'admin-badge-info';
  };

  return (
    <AdminLayout title="Users">
      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Admin Users</h3>
          <button onClick={() => openModal()} className="admin-btn admin-btn-primary">
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add User
          </button>
        </div>
        <div className="admin-card-body" style={{ padding: 0 }}>
          {loading ? (
            <div className="admin-loading">
              <div className="admin-spinner"></div>
            </div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  <th style={{ width: '150px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id}>
                    <td>
                      <div className="admin-flex admin-items-center admin-gap-2">
                        <div
                          className="admin-user-avatar"
                          style={{ width: '36px', height: '36px' }}
                        >
                          {user.name.substring(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight: 500 }}>
                            {user.name}
                            {user.id === currentUser.id && (
                              <span className="admin-text-muted" style={{ fontWeight: 400 }}> (you)</span>
                            )}
                          </div>
                          <div className="admin-text-muted" style={{ fontSize: '12px' }}>{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`admin-badge ${getRoleBadge(user.role)}`}>
                        {user.role}
                      </span>
                    </td>
                    <td>
                      <span className={`admin-badge ${user.isActive ? 'admin-badge-success' : 'admin-badge-warning'}`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="admin-text-muted">
                      {user.lastLoginAt
                        ? new Date(user.lastLoginAt).toLocaleDateString()
                        : 'Never'}
                    </td>
                    <td>
                      <div className="admin-flex admin-gap-2">
                        <button onClick={() => openModal(user)} className="admin-btn admin-btn-sm admin-btn-secondary">
                          Edit
                        </button>
                        <button
                          onClick={() => handleToggleActive(user)}
                          className={`admin-btn admin-btn-sm ${user.isActive ? 'admin-btn-secondary' : 'admin-btn-primary'}`}
                          disabled={user.id === currentUser.id}
                        >
                          {user.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div className="admin-card" style={{ width: '100%', maxWidth: '500px', margin: '20px' }}>
            <div className="admin-card-header">
              <h3 className="admin-card-title">{editingId ? 'Edit User' : 'New User'}</h3>
              <button onClick={closeModal} className="admin-btn admin-btn-icon admin-btn-secondary">×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="admin-card-body">
                <div className="admin-form-group">
                  <label className="admin-label">Name *</label>
                  <input
                    type="text"
                    className="admin-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Full name"
                    required
                  />
                </div>
                <div className="admin-form-group">
                  <label className="admin-label">Email *</label>
                  <input
                    type="email"
                    className="admin-input"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="email@example.com"
                    required
                  />
                </div>
                <div className="admin-form-group">
                  <label className="admin-label">
                    Password {editingId ? '(leave blank to keep current)' : '*'}
                  </label>
                  <input
                    type="password"
                    className="admin-input"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder={editingId ? '••••••••' : 'Min 8 characters'}
                    minLength={editingId ? 0 : 8}
                    required={!editingId}
                  />
                </div>
                <div className="admin-form-group admin-mb-0">
                  <label className="admin-label">Role</label>
                  <select
                    className="admin-select"
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    disabled={editingId === currentUser.id}
                  >
                    <option value="writer">Writer</option>
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                  </select>
                  {editingId === currentUser.id && (
                    <small className="admin-text-muted">You cannot change your own role</small>
                  )}
                </div>
              </div>
              <div className="admin-card-body" style={{ borderTop: '1px solid var(--admin-border)', paddingTop: '16px' }}>
                <div className="admin-flex admin-gap-2 admin-justify-between">
                  <button type="button" onClick={closeModal} className="admin-btn admin-btn-secondary">
                    Cancel
                  </button>
                  <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : (editingId ? 'Update' : 'Create')}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
